import { apiClient } from "../../../shared/api/client";
import { TRANSACTION_ENDPOINTS } from "./endpoints";
import { TransactionItem } from "../types";

export interface FetchTransactionResult {
  data: TransactionItem | null;
  sourceEndpoint: string | null;
  rawResponse: any;
}

const extractData = (res: any): any => {
  return res?.data?.data ?? res?.data?.transaction ?? res?.data ?? null;
};

/**
 * Always executes a live network request to fetch the freshest transaction
 * data by its URL ID parameter, with comprehensive console logging.
 */
export const fetchTransactionById = async (
  id?: string
): Promise<FetchTransactionResult> => {
  if (!id) {
    console.warn("⚠️ [fetchTransactionById] No ID provided in URL path.");
    return { data: null, sourceEndpoint: null, rawResponse: null };
  }

  console.group(`📡 [fetchTransactionById] Initiating live API fetch for ID: "${id}"`);
  console.log("Timestamp:", new Date().toISOString());

  // Sequential candidate endpoints used across legacy and v2 TradeVu services
  const candidates = [
    { name: "SINGLE_TRANSACTION", url: TRANSACTION_ENDPOINTS.SINGLE_TRANSACTION(id) },
    { name: "FX_TRANSFER", url: `/v1/admin/transaction/get_single_fx_transfer/${id}` },
    { name: "TRANSFER_REQUEST", url: `/v1/admin/transaction/get_single_transfer_request/${id}` },
    { name: "FUND_REQUEST", url: `/v1/admin/transaction/fund_request/single/${id}` },
  ];

  for (const candidate of candidates) {
    try {
      console.log(`[fetchTransactionById] Querying ${candidate.name} (${candidate.url})...`);
      const response = await apiClient.get(candidate.url);
      const payload = extractData(response);

      if (payload && (payload._id || payload.id || payload.amount !== undefined)) {
        console.log(`✅ [fetchTransactionById] Live data successfully retrieved from ${candidate.name}:`, payload);
        console.groupEnd();
        return {
          data: {
            ...payload,
            _id: payload._id || payload.id || id,
          } as TransactionItem,
          sourceEndpoint: candidate.url,
          rawResponse: payload,
        };
      }
    } catch (err: any) {
      console.log(
        `[fetchTransactionById] Endpoint ${candidate.name} skipped (${err?.response?.status || err?.message || "error"}).`
      );
    }
  }

  console.info(`ℹ️ [fetchTransactionById] Backend returned 404/empty for ID "${id}".`);
  console.groupEnd();

  return { data: null, sourceEndpoint: null, rawResponse: null };
};
